import { useEffect, useState } from "react";

type Opt = { id: string; name: string };

export function DiscoverInput({
  id,
  ariaLabel,
  instanceId,
  discoverKind,
  discover,
  value,
  onChange,
}: {
  /** Binds the rendered control to a visible <label htmlFor>. The control swaps
   *  between an <input> and a <select> as discovery resolves; only one is ever
   *  mounted, so the id stays unique either way. */
  id?: string;
  ariaLabel: string;
  instanceId?: string;
  discoverKind: string;
  discover?: (instanceId: string, kind: string) => Promise<{ items: Opt[] }>;
  value: string;
  onChange: (v: string) => void;
}) {
  const [options, setOptions] = useState<Opt[] | null>(null);
  const [freeText, setFreeText] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOptions(null);
    setFreeText(false);
    if (!discover || !instanceId) return;
    discover(instanceId, discoverKind)
      .then((r) => { if (!cancelled) setOptions(r.items ?? []); })
      .catch(() => { if (!cancelled) setOptions([]); }); // empty → fall back to text
    return () => { cancelled = true; };
  }, [discover, instanceId, discoverKind]);

  const hasOptions = !!options && options.length > 0;

  if (!hasOptions || freeText) {
    return (
      <div>
        <input id={id} className="field__input" aria-label={ariaLabel} value={value} onChange={(e) => onChange(e.target.value)} />
        {hasOptions && (
          <button type="button" className="btn btn--ghost" onClick={() => setFreeText(false)}>Choose from list</button>
        )}
      </div>
    );
  }

  // A stored value that isn't in the discovered list (e.g. set before the view
  // changed, or hand-entered earlier) must stay visible + selected rather than
  // silently reverting to "—". Surface it as its own option.
  const unmatched = value !== "" && !options!.some((o) => o.id === value);

  return (
    <div>
      <select
        id={id}
        className="field__input"
        aria-label={ariaLabel}
        value={value === "" ? "" : value}
        onChange={(e) => {
          // Identify the sentinel by attribute, not value — a discovered id could
          // legitimately equal any string. Switch to free text WITHOUT clearing so
          // the current value carries over into the text input (edit, not retype).
          const option = e.currentTarget.options[e.currentTarget.selectedIndex];
          if (option?.dataset.freeText === "true") { setFreeText(true); return; }
          onChange(e.target.value);
        }}
      >
        <option value="">—</option>
        {options!.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        {unmatched && <option value={value}>{value} (current)</option>}
        <option data-free-text="true" value="__other__">Type a value…</option>
      </select>
    </div>
  );
}
